// @ts-nocheck
import { create } from "zustand";
import { parseAgentCountryDirectionOptions } from "../../utilities/agentCountryCode";

const STORAGE_KEYS = {
  webrtcPhoneStartTime: "cti_webrtcPhoneStartTime",
  idleSessionStartedAt: "cti_idleSessionStartedAt",
  pbxName: "cti_pbxName",
};

const safeStorage = {
  getItem(key) {
    try {
      if (typeof window === "undefined" || !window?.localStorage) return null;
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem(key, value) {
    try {
      if (typeof window === "undefined" || !window?.localStorage) return;
      window.localStorage.setItem(key, value);
    } catch {
      // ignore
    }
  },
  removeItem(key) {
    try {
      if (typeof window === "undefined" || !window?.localStorage) return;
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
  },
};

const initialWebrtcPhoneStartTime = safeStorage.getItem(STORAGE_KEYS.webrtcPhoneStartTime) || "";
const initialIdleSessionStartedAt =
  safeStorage.getItem(STORAGE_KEYS.idleSessionStartedAt) ||
  initialWebrtcPhoneStartTime ||
  "";

let callSetupPendingTimer: any = null;

export const useCallStore = create((set, get) => ({
  //State
  callState: "idle",
  localHangupRequested: false,
  callerNumber: null,
  callerContact: null,
  callerContactReceivedAt: null,
  session: null,
  profileLogoutPending: false,
  setProfileLogoutPending: (pending) => set({ profileLogoutPending: Boolean(pending) }),
  // Outgoing (webrtc dialer) helpers
  isPlacingCall: false,
  callSetupPending: false,
  outgoingCallStatus: "",
  outgoingRingingActive: false,
  incomingReceivedAt: null,
  sipHeaders: null,
  sipCampaignId: null,
  sipCallGroupId: null,
  sipLeadData: null,
  isAnswering: false,
  ringingActionsSuppressed: false,
  customerRingingActive: false,
  isOnBreak: false,
  channelId: null,
  agentChannelId: null,
  bPartyChannelId: null,
  customerChannelId: null,
  pbxName: safeStorage.getItem(STORAGE_KEYS.pbxName) || null,
  voicemailMeta: null,
  voicemailSentForActiveCall: false,
  markActiveCallSentToVoicemail: () => set({ voicemailSentForActiveCall: true }),
  leadsScreenOpen: false,
  followUpsScreenOpen: false,
  realtimeStatsScreenOpen: false,
  voicemailScreenOpen: false,
  workspacePanel: null,
  activeLeadContext: null,
  activeFollowUpContext: null,
  idleLayoutEditorOpen: false,
  setIdleLayoutEditorOpen: (v) => set({ idleLayoutEditorOpen: Boolean(v) }),
  setLeadsScreenOpen: (v) => set({ leadsScreenOpen: Boolean(v) }),
  setFollowUpsScreenOpen: (v) => set({ followUpsScreenOpen: Boolean(v) }),
  setRealtimeStatsScreenOpen: (v) => set({ realtimeStatsScreenOpen: Boolean(v) }),
  setVoicemailScreenOpen: (v) => set({ voicemailScreenOpen: Boolean(v) }),
  setWorkspacePanel: (v) => set({ workspacePanel: v || null }),
  setActiveLeadContext: (ctx) => set({ activeLeadContext: ctx || null }),
  clearActiveLeadContext: () => set({ activeLeadContext: null }),
  setActiveFollowUpContext: (ctx) => set({ activeFollowUpContext: ctx || null }),
  clearActiveFollowUpContext: () => set({ activeFollowUpContext: null }),

  autoDialNextLead: null,
  setAutoDialNextLead: (v) => set({ autoDialNextLead: v || null }),
  leadsRefreshToken: 0,
  bumpLeadsRefresh: () => set((s) => ({ leadsRefreshToken: s.leadsRefreshToken + 1 })),
  followUpsRefreshToken: 0,
  bumpFollowUpsRefresh: () => set((s) => ({ followUpsRefreshToken: s.followUpsRefreshToken + 1 })),

  // True while a disposition form is being submitted (POST in flight)
  dispositionSubmitting: false,
  dispositionOpen: false,
  dispositionActive: false,
  wrapUpHoldUntil: null,

  // Mirrors AgentPortalPage.tsx's `isOutboundEnabled` (agentCallsData /
  // userData) into the store so softphone/demo-1.ts — a vanilla module with
  // its own raw DOM click listener on the Call button, outside React's
  // props/disabled system — can check it before placing an outbound call.
  isOutboundEnabled: false,
  setOutboundEnabled: (v) => set({ isOutboundEnabled: Boolean(v) }),

  // --- WebRTC media quality (getStats) ---
  // Live sample pushed by softphone/demo-1's startCallQualityMonitor onSample.
  // Consumed by CallQualityIndicator + AgentCallStatsPanel to render MOS/bars.
  callQuality: null,
  callQualityWarnings: [],
  lastCallQualitySummary: null,

  webrtcPhoneStatus:"",
  webrtcPhoneStartTime: initialWebrtcPhoneStartTime,

  idleSessionStartedAt: initialIdleSessionStartedAt,
  idleStatsSentForIncomingAt: null,
  idleResetDeferred: false,

  callCreatedBy: null,
  direction: null,
  activeCallMeta: null,
  // IVR keys the customer pressed (socket `validDtmf` on DIALING/RINGING): { traceId, keys }.
  ivrKeys: null,
  completedCallToPersist: null,
  webrtcMuteMs: 0,
  webrtcMuteStartedAt: null,
  lastCallType: null,
  lastIsIvrSource: false,
  // Set true when the agent declines / sends to voicemail from the ringing
  // screen. Consumed by completeActiveCall so the SIP-hangup path (which can
  // run before/after/twice relative to the React click handler) cannot
  // mislabel an agent-declined call as "missed". Reset on every new call.
  declineIntent: false,
  activeCliId: null,
  activeCliNumber: null,
  activeCountryDirectionId: null,
  activeCallingCode: null,

  setAnswering: (value) => set({ isAnswering: value }),
  setRingingActionsSuppressed: (value) =>
    set({ ringingActionsSuppressed: Boolean(value) }),
  setCustomerRinging: (value) =>
    set({ customerRingingActive: Boolean(value) }),
  setOnBreak: (value) => set({ isOnBreak: Boolean(value) }),
  setDispositionSubmitting: (value) => set({ dispositionSubmitting: Boolean(value) }),
  setDispositionOpen: (value) => set({ dispositionOpen: Boolean(value) }),
  setDispositionActive: (value) => set({ dispositionActive: Boolean(value) }),
  setWrapUpHoldUntil: (value) => set({ wrapUpHoldUntil: value || null }),
  setIdleResetDeferred: (value) => set({ idleResetDeferred: Boolean(value) }),

  setCallState: (callState) => set({ callState }),
  setSession: (session) => set({ session }),

  // --- WebRTC media quality (getStats) ---
  setCallQuality: (sample, warnings) =>
    set({
      callQuality: sample || null,
      callQualityWarnings: Array.isArray(warnings)
        ? warnings
        : Array.isArray(sample?.warnings)
          ? sample.warnings
          : [],
    }),
  clearCallQuality: () => set({ callQuality: null, callQualityWarnings: [] }),
  setLastCallQualitySummary: (summary) =>
    set({ lastCallQualitySummary: summary || null }),

  requestLocalHangup: () => set({ localHangupRequested: true }),

  setPlacingCall: (value) => set({ isPlacingCall: Boolean(value) }),
  setCallSetupPending: (value) => {
    clearTimeout(callSetupPendingTimer);
    if (value) callSetupPendingTimer = setTimeout(() => set({ callSetupPending: false }), 20000);
    set({ callSetupPending: Boolean(value) });
  },
  setOutgoingCallStatus: (status) => set({ outgoingCallStatus: status ? String(status) : "" }),
  setOutgoingRinging: (value) => set({ outgoingRingingActive: Boolean(value) }),

  setWebrtcPhoneStatus: (status) =>
    set({
      webrtcPhoneStatus: status,
    }),

  setWebrtcPhoneStartTime: (time) =>
    set(() => {
      if (!time) {
        safeStorage.removeItem(STORAGE_KEYS.webrtcPhoneStartTime);
        safeStorage.removeItem(STORAGE_KEYS.idleSessionStartedAt);
        return {
          webrtcPhoneStartTime: "",
          idleSessionStartedAt: "",
        };
      }

      const storedIdle = safeStorage.getItem(STORAGE_KEYS.idleSessionStartedAt);
      const storedWebrtc = safeStorage.getItem(STORAGE_KEYS.webrtcPhoneStartTime);
      const effectiveTime = storedIdle || storedWebrtc || time;

      safeStorage.setItem(STORAGE_KEYS.webrtcPhoneStartTime, effectiveTime);
      safeStorage.setItem(STORAGE_KEYS.idleSessionStartedAt, effectiveTime);

      return {
        webrtcPhoneStartTime: effectiveTime,
        idleSessionStartedAt: effectiveTime,
      };
    }),

  setIdleSessionStartedAt: (time) =>
    set(() => {
      if (!time) {
        safeStorage.removeItem(STORAGE_KEYS.idleSessionStartedAt);
        return { idleSessionStartedAt: "" };
      }

      safeStorage.setItem(STORAGE_KEYS.idleSessionStartedAt, time);
      return { idleSessionStartedAt: time };
    }),

  markIdleStatsSentForIncomingAt: (receivedAt) =>
    set({
      idleStatsSentForIncomingAt: receivedAt,
    }),

  setCallCreatedBy: (userId) =>
    set({
      callCreatedBy: userId,
    }),

  setActiveCountryDirection: (direction, agentId) =>
    set(() => {
      if (!direction?.id) {
        if (agentId) {
          safeStorage.removeItem(`agent_active_country_${agentId}`);
        }
        return { activeCountryDirectionId: null, activeCallingCode: null };
      }
      const payload = {
        id: String(direction.id),
        callingCode: String(direction.callingCode || "").replace(/^\+/, ""),
      };
      if (agentId) {
        safeStorage.setItem(
          `agent_active_country_${agentId}`,
          JSON.stringify(payload)
        );
      }
      return {
        activeCountryDirectionId: payload.id,
        activeCallingCode: payload.callingCode,
      };
    }),

  loadActiveCountryForAgent: (agentId, agentCountryCodeDirections = []) =>
    set(() => {
      const options = parseAgentCountryDirectionOptions(agentCountryCodeDirections);
      if (!agentId || !options.length) {
        return { activeCountryDirectionId: null, activeCallingCode: null };
      }

      let stored = null;
      try {
        const raw = safeStorage.getItem(`agent_active_country_${agentId}`);
        if (raw) stored = JSON.parse(raw);
      } catch {
        stored = null;
      }

      const pick =
        stored && options.some((o) => o.id === String(stored.id))
          ? options.find((o) => o.id === String(stored.id))
          : options[0];

      const payload = {
        id: pick.id,
        callingCode: pick.callingCode,
      };
      safeStorage.setItem(
        `agent_active_country_${agentId}`,
        JSON.stringify(payload)
      );
      return {
        activeCountryDirectionId: payload.id,
        activeCallingCode: payload.callingCode,
      };
    }),

  setActiveCli: (cli, agentId) =>
    set(() => {
      if (!cli?.id) {
        if (agentId) {
          safeStorage.removeItem(`agent_active_cli_${agentId}`);
        }
        return { activeCliId: null, activeCliNumber: null };
      }
      const payload = {
        id: Number(cli.id),
        actual_number: String(cli.actual_number || ""),
      };
      if (agentId) {
        safeStorage.setItem(
          `agent_active_cli_${agentId}`,
          JSON.stringify(payload)
        );
      }
      return {
        activeCliId: payload.id,
        activeCliNumber: payload.actual_number,
      };
    }),

  loadActiveCliForAgent: (agentId, assignedClis = []) =>
    set(() => {
      if (!agentId || !Array.isArray(assignedClis) || !assignedClis.length) {
        return { activeCliId: null, activeCliNumber: null };
      }

      let stored = null;
      try {
        const raw = safeStorage.getItem(`agent_active_cli_${agentId}`);
        if (raw) stored = JSON.parse(raw);
      } catch {
        stored = null;
      }

      const defaultCli =
        assignedClis.find((c) => c.is_default) || assignedClis[0];
      const pick =
        stored &&
        assignedClis.some((c) => Number(c.id) === Number(stored.id))
          ? assignedClis.find((c) => Number(c.id) === Number(stored.id))
          : defaultCli;

      if (!pick) {
        return { activeCliId: null, activeCliNumber: null };
      }

      const payload = {
        id: Number(pick.id),
        actual_number: String(pick.actual_number || ""),
      };
      safeStorage.setItem(
        `agent_active_cli_${agentId}`,
        JSON.stringify(payload)
      );
      return {
        activeCliId: payload.id,
        activeCliNumber: payload.actual_number,
      };
    }),

  startActiveCall: (meta) =>
    set((state) => {
      const resolvedDirection = meta?.direction || state.direction || null;
      return {
        direction: resolvedDirection,
        activeCallMeta: {
          ...(meta || {}),
          direction: resolvedDirection,
          start_time: meta?.start_time || new Date().toISOString(),
          created_by: meta?.created_by || get().callCreatedBy || null,
        },
        webrtcMuteMs: 0,
        webrtcMuteStartedAt: null,
      };
    }),

  startWebrtcMute: () =>
    set((state) =>
      state.webrtcMuteStartedAt ? {} : { webrtcMuteStartedAt: Date.now() }
    ),

  stopWebrtcMute: () =>
    set((state) => {
      if (!state.webrtcMuteStartedAt) return {};
      return {
        webrtcMuteMs: state.webrtcMuteMs + (Date.now() - state.webrtcMuteStartedAt),
        webrtcMuteStartedAt: null,
      };
    }),

  updateActiveCallMeta: (partial = {}) =>
    set((state) => ({
      activeCallMeta: state.activeCallMeta
        ? { ...state.activeCallMeta, ...(partial || {}) }
        : state.activeCallMeta,
    })),


  setCallTypeInfo: ({ callType, isIvrSource } = {}) =>
    set((state) => {
      const next = {};
      if (callType) {
        next.lastCallType = callType;
        if (state.activeCallMeta) {
          next.activeCallMeta = { ...state.activeCallMeta, call_type: callType };
        }
      }
      if (isIvrSource) {
        next.lastIsIvrSource = true;
        const base = next.activeCallMeta || state.activeCallMeta;
        if (base) next.activeCallMeta = { ...base, is_ivr_source: true };
      }
      return next;
    }),

  markActiveCallAnswered: (meta = {}) =>
    set((state) => ({
      activeCallMeta: state.activeCallMeta
        ? {
            ...state.activeCallMeta,
            answer_status: meta.answer_status || "answered",
            bridge_time: meta.bridge_time || state.activeCallMeta.bridge_time || new Date().toISOString(),
          }
        : state.activeCallMeta,
    })),

  markActiveCallDeclinedByAgent: () =>
    set((state) => {
      // Always record the intent. completeActiveCall consults this flag, so the
      // SIP-hangup path cannot mislabel the call as "missed" regardless of
      // whether it runs before, after, or twice relative to this call.
      const base = { declineIntent: true };

      // Normal case: the call is still active — stamp the decline cause too.
      if (state.activeCallMeta) {
        return {
          ...base,
          activeCallMeta: {
            ...state.activeCallMeta,
            answer_status: "not_answered",
            hangup_cause: "declined_by_agent",
            is_declined: true,
            is_missed: false,
          },
        };
      }
      // Race: the SIP hangup already ran completeActiveCall and produced a
      // pending "missed" record before this intent was recorded. Patch it so an
      // unanswered agent-declined call is not mislabelled as missed.
      if (
        state.completedCallToPersist &&
        state.completedCallToPersist.answer_status !== "answered" &&
        !state.completedCallToPersist.bridge_time
      ) {
        return {
          ...base,
          completedCallToPersist: {
            ...state.completedCallToPersist,
            answer_status: "not_answered",
            hangup_cause: "declined_by_agent",
            is_declined: true,
            is_missed: false,
          },
        };
      }
      return base;
    }),

  completeActiveCall: (meta = {}) =>
    set((state) => {
      const endTime = meta.end_time || new Date().toISOString();
      const active = state.activeCallMeta || {};
      const bridge = active.bridge_time ? new Date(active.bridge_time) : null;
      const to = endTime ? new Date(endTime) : null;

      const resolvedAnswerStatus = meta.answer_status || active.answer_status || "";
      const isAnswered =
        resolvedAnswerStatus === "answered" ||
        Boolean(active.bridge_time && bridge && !Number.isNaN(bridge.getTime()));

      const talkDuration =
        isAnswered && bridge && to && !Number.isNaN(bridge.getTime()) && !Number.isNaN(to.getTime())
          ? Math.max(0, Math.floor((to.getTime() - bridge.getTime()) / 1000))
          : 0;

      const hangupCause =
        meta.hangup_cause ||
        active.hangup_cause ||
        (isAnswered
          ? "hangup"
          : state.declineIntent
            ? "declined_by_agent"
            : "missed");

      const dir = String(
        meta.direction || active.direction || state.direction || ""
      ).toLowerCase();
      const isIncoming = dir === "incoming" || dir === "inbound" || dir === "in";
      const declinedCause = String(hangupCause).toLowerCase().includes("declined");
      const isDeclined =
        meta.is_declined ??
        active.is_declined ??
        (!isAnswered && (state.declineIntent || declinedCause || !isIncoming));
      const isMissed =
        meta.is_missed ??
        active.is_missed ??
        (!isAnswered && !isDeclined && isIncoming);

      const realSipCallId = state.sipHeaders?.["call-id"] || null;

      const openMuteMs = state.webrtcMuteStartedAt
        ? Date.now() - state.webrtcMuteStartedAt
        : 0;
      const webrtcMuteDuration = Math.round(
        (state.webrtcMuteMs + openMuteMs) / 1000
      );

      const completed = {
        ...active,
        ...meta,
        call_sid: realSipCallId || meta.call_sid || active.call_sid || null,
        end_time: endTime,
        answer_status: resolvedAnswerStatus || (isAnswered ? "answered" : "not_answered"),
        hangup_cause: hangupCause,
        is_declined: isDeclined,
        is_missed: isMissed,
        bridge_ring_duration: meta.bridge_ring_duration ?? null,
        patch_duration: meta.patch_duration ?? null,
        billing_duration: meta.billing_duration ?? null,
        local_talk_seconds: talkDuration,
        call_type: meta.call_type || active.call_type || state.lastCallType || null,
        is_ivr_source:
          meta.is_ivr_source ?? active.is_ivr_source ?? state.lastIsIvrSource ?? false,
        mute_duration: webrtcMuteDuration,
      };

      return {
        completedCallToPersist: completed,
        activeCallMeta: null,
        lastCallType: null,
        lastIsIvrSource: false,
        webrtcMuteMs: 0,
        webrtcMuteStartedAt: null,
      };
    }),

  callLogRefreshToken: 0,

  clearCompletedCallToPersist: () =>
    set((s) => ({
      completedCallToPersist: null,
      voicemailSentForActiveCall: false,
      callLogRefreshToken: s.callLogRefreshToken + 1,
    })),

  // Set by useSnoop.ts (team-lead dashboard) while Monitor/Whisper/Barge is
  // bridging the lead into an agent's live call — AgentPortalPage.tsx's own
  // script-fetch effect used to always resolve the lead's OWN last-call
  // campaign/group context, showing the lead their own last script instead
  // of the monitored agent's, since a snoop session carries no SIP
  // campaign/call-group of its own on the lead's side.
  activeSnoopContext: null,
  setActiveSnoopContext: (ctx) => set({ activeSnoopContext: ctx || null }),

  setSipData: ({ sipHeaders, sipCampaignId, sipCallGroupId, sipLeadData, channelId, agentChannelId, bPartyChannelId, customerChannelId, pbxName } = {}) => {
    if (pbxName) safeStorage.setItem(STORAGE_KEYS.pbxName, String(pbxName));
    return set((state) => ({
      customerChannelId: customerChannelId ?? state.customerChannelId ?? null,
      sipHeaders: sipHeaders ?? state.sipHeaders ?? null,
      sipCampaignId: sipCampaignId ?? state.sipCampaignId ?? null,
      sipCallGroupId: sipCallGroupId ?? state.sipCallGroupId ?? null,
      sipLeadData: sipLeadData ?? state.sipLeadData ?? null,
      bPartyChannelId: bPartyChannelId ?? state.bPartyChannelId ?? null,
      channelId: channelId ?? state.channelId ?? null,
      agentChannelId: agentChannelId ?? state.agentChannelId ?? null,
      pbxName: pbxName ?? state.pbxName ?? null,
    }));
  },

  setCallerContact: (contact, receivedAt) =>
    set({
      callerContact: contact ?? null,
      callerContactReceivedAt: receivedAt || new Date().toISOString(),
    }),

  //Incoming Call
  setIncomingCall: ({ number, session, receivedAt, channelId, agentChannelId, customerChannelId, callGroupId }) =>
    set((state) => ({
      callState: "incoming",
      localHangupRequested: false,
      direction: "incoming",
      callerNumber: number,
      callerContact: null,
      callerContactReceivedAt: null,
      session: session ?? state.session ?? null,
      incomingReceivedAt: receivedAt || new Date().toISOString(),
      idleStatsSentForIncomingAt: null,
      sipHeaders: state.sipHeaders ?? null,
      sipCampaignId: state.sipCampaignId ?? null,
      sipCallGroupId: callGroupId ?? state.sipCallGroupId ?? null,
      sipLeadData: state.sipLeadData ?? null,
      channelId: channelId ?? state.channelId ?? null,
      agentChannelId: agentChannelId ?? state.agentChannelId ?? null,
      customerChannelId: customerChannelId ?? state.customerChannelId ?? null,
      voicemailSentForActiveCall: false,
      voicemailMeta: {
        channelId: channelId ?? null,
        agentChannelId: agentChannelId ?? null,
        customerChannelId: customerChannelId ?? state.customerChannelId ?? null,
        callGroupId: callGroupId ?? null,
        from: number ?? null,
      },
      isPlacingCall: false,
      callSetupPending: false,
      outgoingCallStatus: "",
      outgoingRingingActive: false,
      declineIntent: false,
      ringingActionsSuppressed: false,
      customerRingingActive: false,
      activeCallMeta: state.activeCallMeta
        ? { ...state.activeCallMeta, direction: "incoming" }
        : state.activeCallMeta,
    })),

  // Outgoing Call (Dialer)
  setOutgoingCall: ({ number, session, startedAt } = {}) =>
    set((state) => ({
      callState: "dialing",
      localHangupRequested: false,
      direction: "outgoing",
      callerNumber: number ?? state.callerNumber ?? null,
      callerContact: null,
      callerContactReceivedAt: null,
      session: session ?? state.session ?? null,
      incomingReceivedAt: null,
      idleStatsSentForIncomingAt: null,
      declineIntent: false,
      ringingActionsSuppressed: false,
      customerRingingActive: false,
      activeCallMeta: state.activeCallMeta
        ? { ...state.activeCallMeta, direction: "outgoing" }
        : state.activeCallMeta,
    })),

  //Call Connected
  setConnected: () =>
    set({
      callState: "oncall",
      outgoingRingingActive: false,
      isAnswering: false,
      ringingActionsSuppressed: false,
      customerRingingActive: false,
    }),

  //Hold
  setHold: (held) =>
    set({
      callState: held ? "hold" : "connected",
    }),

  // End Call
  endCall: (options = {}) =>
    set((state) => {
      const wantsReset = options?.resetIdle !== false;
      const dispositionActive = Boolean(state.dispositionActive);
      const shouldResetIdle = wantsReset && !dispositionActive;
      const nextIdleStart = shouldResetIdle ? new Date().toISOString() : state.idleSessionStartedAt;
      const nextIdleResetDeferred = wantsReset
        ? dispositionActive
        : state.idleResetDeferred;

      if (shouldResetIdle) {
        safeStorage.setItem(STORAGE_KEYS.idleSessionStartedAt, nextIdleStart);
      }

      return {
        callState: "idle",
        ivrKeys: null,
        localHangupRequested: false,
        direction: null,
        callerNumber: null,
        callerContact: null,
        callerContactReceivedAt: null,
        session: null,
        isPlacingCall: false,
        callSetupPending: false,
        outgoingCallStatus: "",
        outgoingRingingActive: false,
        incomingReceivedAt: null,
        sipHeaders: null,
        sipCampaignId: null,
        sipCallGroupId: null,
        channelId: null,
        agentChannelId: null,
        bPartyChannelId: null,
        customerChannelId: null,
        sipLeadData: null,
        idleSessionStartedAt: nextIdleStart,
        idleStatsSentForIncomingAt: null,
        idleResetDeferred: nextIdleResetDeferred,
        ringingActionsSuppressed: false,
        customerRingingActive: false,
      };
    }),
}));

// Expose the store on window for quick DevTools inspection. Read it as:
//   __callStore.getState().webrtcPhoneStatus
// This is purely diagnostic — the softphone (features/cti/softphone/demo-1)
// and every consumer imports useCallStore directly; nothing runtime-critical
// depends on this global.
if (typeof window !== "undefined") {
  (window as any).__callStore = useCallStore;
}

// Call states that mean the agent is actively on a live call.
export const ACTIVE_CALL_STATES = ["oncall", "connected", "hold", "conference"];

export const resolveVoicemailChannels = (state?: any) => {
  const s = state || useCallStore.getState();
  const vm = s.voicemailMeta || {};
  const customer =
    vm.customerChannelId || s.customerChannelId || s.sipHeaders?.["x-ns-channelid"] || null;
  const agent =
    [s.bPartyChannelId, vm.agentChannelId, s.agentChannelId, vm.channelId, s.channelId]
      .find((id) => id && String(id) !== String(customer)) || null;
  return { channelId: customer, agentChannelId: agent };
};

export const selectVoicemailReady = (s: any) => {
  const c = resolveVoicemailChannels(s);
  return Boolean(c.channelId && c.agentChannelId);
};

// Returns true if the agent is on an active call.
export const isCallActive = (state?: any) => {
  const s = state || useCallStore.getState();
  return ACTIVE_CALL_STATES.includes(s.callState);
};

export const WRAP_UP_HOLD_DIAL_MESSAGE = 'Wrap-up in progress. End it from the wrap-up banner to make a call.';
export const isWrapUpHoldActive = (state?: any) => {
  const s = state || useCallStore.getState();
  return Boolean(s.wrapUpHoldUntil) && s.wrapUpHoldUntil > Date.now();
};

export const isCallInProgress = (state?: any) => {
  const s = state || useCallStore.getState();
  return s.callState === "incoming" || s.callState === "dialing" || Boolean(s.isPlacingCall);
};

// Single source of truth: should tab close / refresh / logout be blocked?
// Blocked while a call is ringing/dialing/being placed, while on an active
// call, while a disposition is being submitted,
// or while one is still open/pending (dispositionActive covers the whole
// wrap-up window — set the moment a disposition context opens and cleared
// only on submit — whereas dispositionOpen is only true while the
// disposition screen specifically is the one currently showing; an agent
// who navigated elsewhere mid wrap-up still has dispositionActive true).
export const isExitBlocked = (state?: any) => {
  const s = state || useCallStore.getState();
  return isCallActive(s) || isCallInProgress(s) || Boolean(s.dispositionActive) || Boolean(s.dispositionOpen) || Boolean(s.dispositionSubmitting);
};

// Human-readable reason for the block (for in-app messaging).
export const getExitBlockReason = (state?: any) => {
  const s = state || useCallStore.getState();
  if (isCallActive(s)) return "You are on an active call.";
  if (s.callState === "incoming") return "A call is ringing.";
  if (isCallInProgress(s)) return "A call is being connected.";
  if (s.dispositionSubmitting) return "Disposition is being submitted.";
  if (s.dispositionActive || s.dispositionOpen) return "You have an active disposition to complete.";
  return "";
};

// Bridge for non-module bundled code (e.g. SIP.js demo-*.js) that can't import
// this store. Lets the SIP UserAgent's autoStop beforeunload handler skip its
// teardown when the user is on a call / submitting a disposition — otherwise it
// would stop the UA even when the user cancels the leave/refresh prompt.
if (typeof window !== "undefined") {
  window.__ctiIsExitBlocked = () => isExitBlocked();
}
